import axiosInstance from "../../../core/api/axios";
import { setAuthToken } from '../../../core/commom/authToken';
import TrackEventsUser from '../../../core/track/trackEventsUser';
import {
    ILoginCredentials,
    LoginResponse,
    SsoExchangeResponse,
    SocialAuthUrlResponse,
    SocialCallbackPayload,
} from "../models/user.model";
import { SocialProviderId } from "../constants/socialAuth";

export function persistAuthToken(accessToken: string) {
    setAuthToken(accessToken);
    TrackEventsUser("LOGIN_SUCCESS", "v2");
}

export async function login(credentials: ILoginCredentials): Promise<LoginResponse> {
    const response =  await axiosInstance.post('/auth/login', {
        ...credentials,
        application: 'web',
    })

    if(response.data && response.data.access_token) {
        persistAuthToken(response.data.access_token);
    }

    return response.data
}

/*
 * Os dois endpoints de login social têm o mesmo contrato nos dois provedores —
 * só o segmento do provedor muda na URL. Uma função por provedor seria a mesma
 * chamada quatro vezes; o id entra como parâmetro.
 *
 * POST /auth/{provider}/url      { application } -> { url }
 * POST /auth/{provider}/callback { code, state, application } -> LoginResponse
 */
export async function getSocialAuthUrl(provider: SocialProviderId): Promise<SocialAuthUrlResponse> {
    const response = await axiosInstance.post(`/auth/${provider}/url`, {
        application: 'web',
    });

    return response.data;
}

export async function socialCallback(
    provider: SocialProviderId,
    payload: SocialCallbackPayload,
): Promise<LoginResponse> {
    const response = await axiosInstance.post(`/auth/${provider}/callback`, {
        ...payload,
        application: 'web',
    });

    if (!response.data?.access_token) {
        throw new Error(`Failed to authenticate with ${provider}`);
    }

    persistAuthToken(response.data.access_token);

    return response.data;
}

/** Troca um código de handoff app -> navegador (ver mobile sso-token) por uma sessão web. */
export async function ssoExchange(code: string): Promise<SsoExchangeResponse> {
    const response = await axiosInstance.post('/auth/sso-exchange', { code });

    if (!response.data?.access_token) {
        throw new Error('Failed to exchange SSO code');
    }

    persistAuthToken(response.data.access_token);

    return response.data;
}

export async function requestMe(): Promise<any> {
    const response =  await axiosInstance.post('/auth/me')

    // Só o retorno de uma requisição real renova o token. Persistir a partir do
    // cache do React Query regravaria um token já revogado por cima do atual.
    setAuthToken(response.data.access_token)

    // SE O USUÁRIO FOR B2B, REDIRECIONA PARA A PLATAFORMA ANTIGA
    // ESSES USUÁRIOS NÃO DEVEM ACESSAR A NOVA PLATAFORMA
    // if(response.data.isB2B && response.data.B2BCompany === "statera") {
    //     const PLATFORM_URL = env.REACT_APP_PLATFORM_URL
    //     window.location.href = PLATFORM_URL;
    // }

    if (response?.data?.error === 401){
        requestLogout();
        return
    }

    return response.data
}

/** Invalida a sessão no Redis (content). Falha de rede não deve impedir limpeza local. */
export async function requestLogout(): Promise<void> {
    try {
        await axiosInstance.post('/auth/logout');
    } catch {
        // logout é best-effort no servidor; o cliente sempre limpa o token local
    }
}


interface IRequestCodeForgotPassword {
    email: string;
}

export async function requestCodeForgotPassword(body: IRequestCodeForgotPassword): Promise<any> {
    const response =  await axiosInstance.post('/password/forgout', body)
    return response.data
}

interface IRequestValidaCodeForgotPassword {
    email: string;
    code: string
}

export async function requestValidCodeForgotPassword(body: IRequestValidaCodeForgotPassword): Promise<any> {
    const response =  await axiosInstance.post('/password', body)
    return response.data
}

interface IRequestForgotPassword {
    code: string
    password: string
    email: string
}

export async function requestForgotPassword(body: IRequestForgotPassword): Promise<any> {
    const response =  await axiosInstance.put('/password', body)
    return response.data
}

export async function requestCreatePassword(token: string, password: string) {
    try {
        const { data } = await axiosInstance.post(`/user/create-pwd`, { token, password });

        return data;
    } catch(e: any) {
        throw new Error(e);
    }
}
